import React, {PureComponent} from 'react';
import styled from 'styled-components';
import OutsideClickHandler from 'react-outside-click-handler';
import {v4 as uuid} from 'uuid';
import {T} from '../../components/I18n'

import Icon from '../Icon';
import Menu from '../Menu';

const KEY_UP = 38;
const KEY_DOWN = 40;
const KEY_BACKSPACE = 8;

class ChipInput extends PureComponent {
  static defaultProps = {
    labelPosition: 'top'
  };

  id = uuid();
  input = React.createRef();
  suggestionContainer = React.createRef();
  state = {
    focused: false,
    suggestionsOpen: false,
    value: '',
    focusedSuggestion: 0
  };

  setInputState = reducer => {
    const {onChange, value} = this.props;
    onChange(typeof reducer === 'function' ? reducer(value) : reducer);
  }

  handleKeyPress = e => {
    const {suggestions, value: chips} = this.props;

    if (e.keyCode === KEY_BACKSPACE) {
      const {value} = this.state;

      // Only delete the previous chip if the input box is empty.
      this.setInputState(chips => value === '' ? chips.slice(0, -1) : chips);
    }
    else if (suggestions && e.keyCode === KEY_UP) {
      this.setState(prevState => {
        if (!prevState.suggestionsOpen || prevState.focusedSuggestion < 0) {
          return null;
        }

        if (this.suggestionContainer.current) {
          const container = this.suggestionContainer.current.getContainer();
          const distanceFromTop = container.scrollTop;
          const distanceFromBottom = container.scrollTop + container.offsetHeight;
          const targetPosition = (34 * (prevState.focusedSuggestion - 1)) + 8;

          if (targetPosition < distanceFromTop || targetPosition > distanceFromBottom) {
            container.scrollTop = targetPosition;
          }
        }

        return {
          focusedSuggestion: prevState.focusedSuggestion - 1
        };
      });
    }
    else if (suggestions && e.keyCode === KEY_DOWN) {
      this.setState(prevState => {
        const filteredSuggestions = suggestions
          .filter(filterAlreadyUsedSuggestions(chips))
          .filter(filterBySuggestion(prevState.value));

        if (!prevState.suggestionsOpen || prevState.focusedSuggestion >= (filteredSuggestions.length - 1)) {
          return null;
        }

        if (this.suggestionContainer.current) {
          const container = this.suggestionContainer.current.getContainer();
          const distanceFromTop = container.scrollTop;
          const distanceFromBottom = container.scrollTop + container.offsetHeight;
          const targetPosition = (34 * (prevState.focusedSuggestion + 2)) + 8;

          if (targetPosition < distanceFromTop || targetPosition > distanceFromBottom) {
            container.scrollTop = targetPosition - container.offsetHeight;
          }
        }

        return {
          focusedSuggestion: prevState.focusedSuggestion + 1
        };
      });
    }
    else if (e.key === 'Enter') {
      e.preventDefault();
      this.setState(prevState => {
        let value = prevState.value;

        if (suggestions && prevState.suggestionsOpen) {
          value = suggestions
            .filter(filterAlreadyUsedSuggestions(chips))
            .filter(filterBySuggestion(value))[prevState.focusedSuggestion];
        }

        if (!value) {
          return null;
        }

        this.setInputState(chips => [...chips, value]);
        return {
          value: '',
          suggestionsOpen: false
        };
      });
    }
    else {
      this.setState({suggestionsOpen: true, focusedSuggestion: 0});
    }
  }

  handleValueChange = e => {
    this.setState({value: e.target.value});

    if (this.suggestionContainer.current) {
      this.suggestionContainer.current.scheduleUpdate();
    }
  }

  handleRemove = (chip, event) => {
    event.preventDefault();
    this.setInputState(chips => chips.filter(c => c !== chip));
  }

  handleFocus = () => {
    const {onFocus} = this.props;

    this.setState(prevState => ({
      focused: true,
      suggestionsOpen: prevState.value.length > 0,
      focusedSuggestion: 0
    }));
    this.input.current.focus();

    if (onFocus) {
      onFocus();
    }
  }

  handleBlur = () => {
    const {onBlur} = this.props;
    this.setState({focused: false});

    if (onBlur) {
      onBlur();
    }
  }

  handleCloseSuggestions = () => {
    this.setState({suggestionsOpen: false});
  }

  handleChooseSuggestion = suggestion => {
    this.setState({value: '', suggestionsOpen: false});
    this.setInputState(chips => [...chips, suggestion]);
    this.handleFocus();
  }

  renderSuggestions() {
    const {suggestions, value: chips} = this.props;
    const {suggestionsOpen, value, focusedSuggestion} = this.state;
    const filteredSuggestions = suggestions
      .filter(filterAlreadyUsedSuggestions(chips))
      .filter(filterBySuggestion(value));

    if (filteredSuggestions.length < 1) {
      return null;
    }

    return (
      <Suggestions
        fullWidth
        ref={this.suggestionContainer}
        relativeTo={this.id}
        open={suggestionsOpen}
        onClose={this.handleCloseSuggestions}>
        {filteredSuggestions.map((suggestion, i) => (
          <Menu.Option
            key={suggestion}
            active={focusedSuggestion === i}
            onClick={() => this.handleChooseSuggestion(suggestion)}>
            <T noTranslate>{toString(suggestion)}</T>
            
          </Menu.Option>
        ))}
      </Suggestions>
    );
  }

  render() {
    const {placeholder, suggestions, value: chips, id, label, required, labelPosition, invalid, validationText, helpText, style, className, disabled} = this.props;
    const {focused, value} = this.state;

    if (label && !id) {
      throw new Error('When setting a label on an <ChipInput />, you must also include an id.');
    }

    if (labelPosition !== 'top' && labelPosition !== 'start') {
      throw new Error('Valid label positions are top and start');
    }

    return (
      <div>
        <OutsideClickHandler onOutsideClick={this.handleBlur}>
          <Container labelPosition={labelPosition} style={style} className={className}>
            {label && (
              <Label htmlFor={id} labelPosition={labelPosition}>{label + (required ? ' *' : '')}</Label>
            )}
            <Input id={this.id} onClick={this.handleFocus} focused={focused} invalid={invalid ? true : undefined} disabled={disabled}>
              {chips.map((chip, i) => (
                <Chip key={i}>
                  <span> <T noTranslate>{toString(chip)}</T></span>
                  <button onClick={(e) => this.handleRemove(chip, e)}>
                    <Icon name="close" />
                  </button>
                </Chip>
              ))}
              <AutoGrowingInput
                type="text"
                placeholder={chips.length === 0 ? placeholder : undefined}
                ref={this.input}
                value={value}
                onChange={this.handleValueChange}
                onKeyDown={this.handleKeyPress}
                onFocus={this.handleFocus}
                disabled={disabled}
              />
            </Input>
          </Container>
          {((invalid && validationText) || helpText) && (
            <Help invalid={invalid ? true : undefined}>{invalid ? validationText : helpText}</Help>
          )}
        </OutsideClickHandler>
        {suggestions && this.renderSuggestions()}
      </div>
    );
  }
}

function toString(obj) {
  if (obj && obj.toString) {
    return obj.toString();
  }

  return obj;
}

function filterAlreadyUsedSuggestions(valuesInUse) {
  valuesInUse = valuesInUse.map(toString);
  return suggestion => valuesInUse.indexOf(toString(suggestion)) < 0;
}

function filterBySuggestion(value) {
  return suggestion => toString(suggestion).toLowerCase().indexOf(value.toLowerCase()) >= 0;
}

const Container = styled.div`
  display: flex;
  flex-direction: ${props => props.labelPosition === 'top' ? 'column' : 'row'};
`;

const Input = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  background: #fff;
  min-height: calc(2.25rem + 2px);
  padding: .125rem .75rem;
  line-height: 1.5;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: .25rem;
  transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

  ${props => {
    if (props.disabled) {
      return `
        background-color: rgb(235, 235, 228);
      `
    }

    if (props.focused) {
      return `
        color: #495057;
        background-color: #fff;
        border-color: #80bdff;
        outline: 0;
        box-shadow: ${props.invalid ? '0 0 0 0.2rem rgba(220, 53, 69, .25)' : '0 0 0 0.2rem rgba(0, 123, 255, .25)'};
      `;
    }
  }}

  // Handle validation state
  ${props => {
    if (props.invalid) {
      return `border-color: #dc3545 !important;`;
    }
  }}
`;

const Chip = styled.span`
  display: flex;
  background: #0049ee;
  padding: 0 0 0 0.625rem;
  margin: 0.25rem;
  color: #fff;
  border-radius: 1rem;
  line-height: 1;
  height: 1.5rem;
  font-size: 0.875rem;
  align-items: center;

  button {
    background: #fff;
    border-radius: 1rem;
    border: none;
    color: #0049ee;
    padding: 0;
    margin: 0;
    width: 1.2rem;
    height: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.625rem;
    margin-right: 0.125rem;
  }

  button {
    cursor: pointer;
  }
`;

const AutoGrowingInput = styled.input`
  width: ${props => {
    if (props.value && props.value.length > 0) {
      return `${((props.value.length || 1) / 2) + 2}rem`;
    }
    else if (props.placeholder) {
      return `${props.placeholder.length}rem`;
    }

    return '2rem';
  }};
  min-width: 2rem;
  border: 0;
  color: #495057;
  font-size: 1rem;
  margin: 0;
  padding: 0;
  margin-left: 0.25rem;
  line-height: 1.5;
  background: transparent;

  :focus {
    outline: 0;
  }
`;

const Label = styled.label`
  ${props => {
    if (props.labelPosition === 'top') {
      return `
        display: block;
        margin-bottom: 0.5rem;
      `;
    }

    return `
      word-wrap: nowrap;
      margin-right: 1rem;
      margin-top: 0.5rem;
    `;
  }}
`;

const Help = styled.small`
  display: block;
  font-size: 0.875rem;
  color: #747474;
  margin-top: 0.5rem;

  ${props => {
    if (props.invalid) {
      return `
        color: #dc3545;
      `;
    }
  }}
`;

const Suggestions = styled(Menu)`
  max-height: 12rem;
  overflow-y: auto;
`;

export default ChipInput;
